"use client"

import { useState } from "react"
import { ArrowUp, MessageCircle, Share2, Link2, Twitter, Linkedin } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { BookmarkButton } from "@/components/bookmarks/BookmarkButton"
import { useToast } from "@/hooks/useToast"
import { formatNumber } from "@/lib/utils"

interface PostActionsProps {
  postId: string
  slug: string
  title: string
  upvoteCount: number
  commentCount: number
  isUpvoted?: boolean
  onUpvote?: () => void
  onScrollToComments?: () => void
}

export function PostActions({ postId, slug, title, upvoteCount, commentCount, isUpvoted = false, onUpvote, onScrollToComments }: PostActionsProps) {
  const { toast } = useToast()
  const [upvoted, setUpvoted] = useState(isUpvoted)
  const [count, setCount] = useState(upvoteCount)

  const handleUpvote = () => {
    if (upvoted) {
      setCount(c => c - 1)
    } else {
      setCount(c => c + 1)
    }
    setUpvoted(!upvoted)
    onUpvote?.()
  }

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/post/${slug}` : ""

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl)
    toast({ title: "Link copied to clipboard" })
  }

  const handleShareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`, "_blank")
  }

  const handleShareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank")
  }

  return (
    <div className="flex items-center space-x-2">
      {/* Upvote */}
      <Button variant={upvoted ? "default" : "outline"} size="sm" onClick={handleUpvote} className={upvoted ? "upvote-animation" : ""}>
        <ArrowUp className="mr-1 h-4 w-4" />
        {formatNumber(count)}
      </Button>

      {/* Comments */}
      <Button variant="outline" size="sm" onClick={onScrollToComments}>
        <MessageCircle className="mr-1 h-4 w-4" />
        {formatNumber(commentCount)}
      </Button>

      {/* Share */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon">
            <Share2 className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleCopyLink}>
            <Link2 className="mr-2 h-4 w-4" />
            Copy link
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleShareTwitter}>
            <Twitter className="mr-2 h-4 w-4" />
            Share on Twitter
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleShareLinkedIn}>
            <Linkedin className="mr-2 h-4 w-4" />
            Share on LinkedIn
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Bookmark */}
      <BookmarkButton postId={postId} variant="outline" size="icon" />
    </div>
  )
}
